import { Patient } from '@/lib/types/patient';

interface FetchPatientsOptions {
  search?: string;
  dateRange?: [Date | null, Date | null];
  status?: 'all' | 'active' | 'inactive';
}

// Fetch patients list with filtering
export async function fetchPatients(_options: FetchPatientsOptions = {}): Promise<Patient[]> {
  // Simulate API call
  await new Promise(resolve => setTimeout(resolve, 1000));
  
  // Mock data
  const patients: Patient[] = [
    {
      id: 'P1001',
      firstName: 'John',
      lastName: 'Doe',
      dateOfBirth: new Date('1980-05-15'),
      gender: 'male',
      email: 'john.doe@example.com',
      phone: '555-123-4567',
      address: {
        street: '123 Main St',
        city: 'Anytown',
        state: 'CA',
        zipCode: '12345',
      },
      insuranceInfo: {
        provider: 'Health Plus',
        policyNumber: 'HP123456',
      },
      medicalHistory: "Hypertension \n Diabetes",
      allergies: "Penicillin",
      medications: [
        {
          name: 'Metformin',
          dosage: '500mg',
          frequency: 'Twice daily',
        },
      ],
      status: 'active',
      lastVisit: '2024-03-15',
    },
    {
      id: 'P1002',
      firstName: 'Jane',
      lastName: 'Smith',
      dateOfBirth: new Date('1992-08-22'),
      gender: 'female',
      email: 'jane.smith@example.com',
      phone: '555-987-6543',
      address: {
        street: '456 Oak Ave',
        city: 'Somewhere',
        state: 'NY',
        zipCode: '67890',
      },
      insuranceInfo: {
        provider: 'MediCare',
        policyNumber: 'MC789012',
      },
      medicalHistory: "Asthma \n Seasonal allergies",
      allergies: "Pollen, Dust",
      medications: [
        {
          name: 'Albuterol',
          dosage: '90mcg',
          frequency: 'As needed',
        },
      ],
      status: 'inactive',
      lastVisit: '2023-11-30',
    },
    {
      id: 'P1003',
      firstName: 'Robert',
      lastName: 'Johnson',
      dateOfBirth: new Date('1975-03-10'),
      gender: 'male',
      email: 'robert.j@example.com',
      phone: '555-456-7890',
      address: {
        street: '789 Pine St',
        city: 'Elsewhere',
        state: 'TX',
        zipCode: '34567',
      },
      insuranceInfo: {
        provider: 'Blue Shield',
        policyNumber: 'BS345678',
      },
      medicalHistory: "Arthritis \n High cholesterol",
      allergies: "Sulfa drugs",
      medications: [
        {
          name: 'Lipitor',
          dosage: '20mg',
          frequency: 'Daily',
        },
      ],
      status: 'inactive',
      lastVisit: '2023-09-15',
    },
    {
      id: 'P1004',
      firstName: 'Maria',
      lastName: 'Garcia',
      dateOfBirth: new Date('1988-12-05'),
      gender: 'female',
      email: 'maria.g@example.com',
      phone: '555-234-5678',
      address: {
        street: '321 Elm St',
        city: 'Nowhere',
        state: 'FL',
        zipCode: '89012',
      },
      insuranceInfo: {
        provider: 'Aetna',
        policyNumber: 'AE901234',
      },
      medicalHistory: "Migraine \n Depression",
      allergies: "None",
      medications: [
        {
          name: 'Sumatriptan',
          dosage: '50mg',
          frequency: 'As needed',
        },
      ],
      status: 'inactive',
      lastVisit: '2023-10-20',
    }
  ];

  // Filter based on status if provided
  if (_options.status && _options.status !== 'all') {
    return patients.filter(patient => 
      _options.status === 'active' ? patient.status === 'active' : patient.status === 'inactive'
    );
  }

  return patients;
}

// Get a single patient
export async function getPatient(id: string): Promise<Patient> {
  // Simulate API call
  await new Promise(resolve => setTimeout(resolve, 1000));
  
  // Find patient in mock data
  const mockPatients = await fetchPatients();
  const patient = mockPatients.find(p => p.id === id);
  
  if (patient) {
    return patient;
  }
  
  // Fallback to default patient if ID not found
  return {
    id,
    firstName: 'John',
    lastName: 'Doe',
    dateOfBirth: new Date('1980-05-15'),
    gender: 'male',
    email: 'john.doe@example.com',
    phone: '555-123-4567',
    address: {
      street: '123 Main St',
      city: 'Anytown',
      state: 'CA',
      zipCode: '12345',
    },
    insuranceInfo: {
      provider: 'Health Plus',
      policyNumber: 'HP123456',
    },
    medicalHistory: "Hypertension: Visited office with 130/80 BP \n Diabetes",
    allergies: "Penicillin",
    medications: [
      {
        name: 'Metformin',
        dosage: '500mg',
        frequency: 'Twice daily',
      },
    ],
    status: 'active',
    lastVisit: '2024-03-15',
  };
}

// Create a new patient
export async function createPatient(data: Omit<Patient, 'id'>): Promise<Patient> {
  // Simulate API call
  await new Promise(resolve => setTimeout(resolve, 1000));
  return {
    id: `P${Math.floor(Math.random() * 10000)}`,
    ...data,
  };
}

// Update a patient
export async function updatePatient(data: Patient): Promise<Patient> {
  // Simulate API call
  await new Promise(resolve => setTimeout(resolve, 1000));
  return data;
}

// Archive a patient
export async function archivePatient(id: string): Promise<Patient> {
  // Simulate API call
  await new Promise(resolve => setTimeout(resolve, 1000));
  const patient = await getPatient(id);
  return updatePatient({ ...patient, status: 'inactive' });
}

// Unarchive a patient
export async function unarchivePatient(id: string): Promise<Patient> {
  // Simulate API call
  await new Promise(resolve => setTimeout(resolve, 1000));
  const patient = await getPatient(id);
  return updatePatient({ ...patient, status: 'active' });
}
